import { useEffect } from "react";
import { shallow } from "zustand/shallow";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { useAccount } from "wagmi";
import { Box, Stack } from "@chakra-ui/react";
import { Container } from "@looksrare/uikit";
import { Button } from "uikit";
import { useInfiniteWatchlistCollections, useWatchlistBase } from "hooks/watchlists";
import { useDebouncedValue } from "hooks/useDebouncedValue";
import { RealtimeDataStatusProvider } from "components/RealtimeData/context";
import { CollectionTable } from "views/collections/components/CollectionTable";
import { CollectionTableFilters } from "views/collections/components/CollectionTableFilters";
import { getSortFromLabelAndTimeframe, useCollectionViewStore } from "views/collections/shared";
import { WatchlistHeader } from "views/watchlists/components/WatchlistHeader";

interface SingleWatchlistViewProps {
  watchlistId: number;
}

export const SingleWatchlistView = ({ watchlistId }: SingleWatchlistViewProps) => {
  const { t } = useTranslation();
  const { address } = useAccount();
  const { events } = useRouter();
  const [sortLabel, timeframe, term, reset] = useCollectionViewStore(
    (state) => [state.sortLabel, state.timeFrame, state.term, state.reset],
    shallow
  );
  const debouncedTerm = useDebouncedValue<string>(term);
  const sort = getSortFromLabelAndTimeframe(sortLabel, timeframe);

  const { data: watchlistData } = useWatchlistBase({ id: watchlistId, account: address });

  const watchlistCollectionsQuery = useInfiniteWatchlistCollections(
    { id: watchlistId, account: address },
    {
      sort,
      search: {
        term: debouncedTerm,
      },
    }
  );

  const flattenedCollectionsResults = watchlistCollectionsQuery.data?.pages.flat() || [];

  const { name, owner, watchersCount, collectionCount, id, isWatching } = watchlistData || {};

  // Reset filters when you leave the page
  useEffect(() => {
    const resetAllFilters = () => reset();
    events.on("routeChangeComplete", resetAllFilters);
    return () => {
      events.off("routeChangeComplete", resetAllFilters);
    };
  }, [events, reset]);

  return (
    <Box>
      <Container>
        <Box width="100%" py={10}>
          <WatchlistHeader
            account={address}
            name={name}
            owner={owner!}
            watchersCount={watchersCount || 0}
            collectionCount={collectionCount || 0}
            watchlistId={id!}
            isUserWatching={isWatching}
            mb={8}
          />
          <Stack spacing={6}>
            <RealtimeDataStatusProvider>
              <CollectionTableFilters hideIsVerifiedFilter />
              <CollectionTable
                isLoading={watchlistCollectionsQuery.isLoading}
                collections={flattenedCollectionsResults}
                timeframe={timeframe}
              />
            </RealtimeDataStatusProvider>
          </Stack>
          {watchlistCollectionsQuery.hasNextPage && (
            <Box textAlign="center" py={6}>
              <Button
                isLoading={watchlistCollectionsQuery.isFetching}
                size="sm"
                colorScheme="gray"
                isDisabled={!watchlistCollectionsQuery.hasNextPage}
                onClick={() => watchlistCollectionsQuery.fetchNextPage()}
              >
                {t("Load More")}
              </Button>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};
